import {
  CMS_GOOGLE_FONTS,
  resolveAllowedFontWeight,
  resolveCmsFontVariant,
} from "@/lib/cms/cms-theme.constants";
import { Extension } from "@tiptap/react";

const ALLOWED_FAMILIES: readonly string[] = CMS_GOOGLE_FONTS.map(
  (f) => f.googleFontFamily,
);

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    cmsFontFamily: {
      setFontFamily: (fontFamily: string, fontVariant?: string | null) => ReturnType;
      setFontVariant: (fontFamily: string, fontVariant: string) => ReturnType;
      unsetFontFamily: () => ReturnType;
    };
  }
}

export const CmsFontFamily = Extension.create({
  name: "cmsFontFamily",

  addGlobalAttributes() {
    return [
      {
        types: ["textStyle"],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => {
              const raw = element.style.fontFamily;
              if (!raw) return null;
              const family = raw.replace(/['"]/g, "").split(",")[0]?.trim();
              return ALLOWED_FAMILIES.includes(family) ? family : null;
            },
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) return {};
              const fontWeight =
                typeof attributes.fontWeight === "number"
                  ? `; font-weight: ${attributes.fontWeight}`
                  : "";
              return {
                style: `font-family: '${attributes.fontFamily}', sans-serif${fontWeight}`,
              };
            },
          },
          fontWeight: {
            default: null,
            parseHTML: (element) => {
              const raw = element.style.fontWeight;
              if (!raw) return null;
              return resolveAllowedFontWeight(raw);
            },
            renderHTML: () => ({}),
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontFamily:
        (fontFamily: string, fontVariant?: string | null) =>
        ({ chain }) => {
          if (!ALLOWED_FAMILIES.includes(fontFamily)) return false;
          const variant = resolveCmsFontVariant(fontFamily, fontVariant);
          return chain()
            .setMark("textStyle", {
              fontFamily,
              fontWeight: variant.weight,
            })
            .run();
        },
      setFontVariant:
        (fontFamily: string, fontVariant: string) =>
        ({ chain }) => {
          if (!ALLOWED_FAMILIES.includes(fontFamily)) return false;
          const variant = resolveCmsFontVariant(fontFamily, fontVariant);
          return chain()
            .setMark("textStyle", {
              fontFamily,
              fontWeight: variant.weight,
            })
            .run();
        },
      unsetFontFamily:
        () =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { fontFamily: null, fontWeight: null })
            .run(),
    };
  },
});
